import React from 'react';
import { useTranslation } from 'react-i18next';
import { scrollToSection } from '../utils/scrollToSection';
import '../styles/Services.css';

// This component displays six pricing/service cards styled similarly to the provided image.
const Services: React.FC = () => {
  const { t } = useTranslation();

  // Array of service plans, each with translation keys for headings, price, description, and features
  const plans = [
    {
      key: 'basic',
      headerClass: 'card-header-basic',
      price: '5.99',
      features: ['services.basic.feature1', 'services.basic.feature2', 'services.basic.feature3', 'services.basic.feature4'],
    },
    {
      key: 'standard',
      headerClass: 'card-header-standard',
      price: '9.99',
      features: ['services.standard.feature1', 'services.standard.feature2', 'services.standard.feature3', 'services.standard.feature4'],
    },
    {
      key: 'premium',
      headerClass: 'card-header-premium',
      price: '15.99',
      features: ['services.premium.feature1', 'services.premium.feature2', 'services.premium.feature3', 'services.premium.feature4'],
    },
    {
      key: 'special',
      headerClass: 'card-header-special',
      price: '29.99',
      features: ['services.special.feature1', 'services.special.feature2', 'services.special.feature3', 'services.special.feature4'],
    },
    {
      key: 'guidedTours',
      headerClass: 'card-header-guided',
      price: '49.99',
      features: ['services.guidedTours.feature1', 'services.guidedTours.feature2', 'services.guidedTours.feature3', 'services.guidedTours.feature4'],
    },
    {
      key: 'equipment',
      headerClass: 'card-header-equipment',
      price: '19.99',
      features: ['services.equipment.feature1', 'services.equipment.feature2', 'services.equipment.feature3', 'services.equipment.feature4'],
    },
  ];

  // Use the translation file for the currency symbol so it updates with the language
  const currencySymbol = t('currency');

  return (
    <section id="services" className="services-section">
      <div className="services-container">
        {/* Section heading using translation key */}
        <h2>{t('services.title')}</h2>
        <div className="services-cards-grid">
          {/* Render each plan as a card */}
          {plans.map((plan, idx) => (
            <div className="service-card-modern" key={plan.key}>
              {/* Card header with colourful background */}
              <div className={`service-card-header ${plan.headerClass}`}>
                {t(`services.${plan.key}.title`)}
              </div>
              {/* Price section with dynamic currency symbol from translation */}
              <div className="service-card-price">
                <span className="currency">{currencySymbol}</span>
                <span className="price-value">{plan.price}</span>
                <span className="per-month">/ {t('services.perMonth')}</span>
              </div>
              {/* Description using translation key */}
              <div className="service-card-description">
                {t(`services.${plan.key}.description`)}
              </div>
              {/* Features list */}
              <ul className="service-card-features">
                {plan.features.map((featureKey, i) => (
                  <li key={i} className="service-card-feature">
                    {/* Tick icon for included features */}
                    <span className="feature-tick">✔</span> {t(featureKey)}
                  </li>
                ))}
              </ul>
              {/* Enquire Now button scrolls to contact form */}
              <button 
                className="service-card-buy-btn"
                onClick={() => scrollToSection('contact')}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { scrollToSection('contact'); }}}
                aria-pressed="false"
                tabIndex={0}
                onTouchStart={e => e.currentTarget.classList.add('pressed')}
                onTouchEnd={e => e.currentTarget.classList.remove('pressed')}
                onMouseDown={e => e.currentTarget.classList.add('pressed')}
                onMouseUp={e => e.currentTarget.classList.remove('pressed')}
                onBlur={e => e.currentTarget.classList.remove('pressed')}
                // British English comment: Ensures button is accessible and gives visual feedback on press
              >
                {t('services.enquireNow')}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services; 